import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LayoutDashboard, Key, User, LogOut, Menu, X, CreditCard, Book } from 'lucide-react'
import { useState } from 'react'
import clsx from 'clsx'
import { Logo } from './Logo'

export function Layout() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'API Keys', href: '/dashboard/api-keys', icon: Key },
    { name: 'Documentation', href: '/dashboard/documentation', icon: Book },
    { name: 'Billing', href: '/pricing', icon: CreditCard },
  ]

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-bg/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:hidden',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex h-full flex-col bg-panel border-r border-border">
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            <Link to="/" onClick={() => setSidebarOpen(false)}><Logo size="md" /></Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-bg transition-colors"
            >
              <X className="h-5 w-5 text-text-muted" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/20'
                      : 'text-text-muted hover:bg-bg hover:text-text border border-transparent'
                  )}
                >
                  <Icon className={clsx(
                    'mr-3 h-5 w-5 transition-colors',
                    isActive ? 'text-accent' : 'text-text-muted group-hover:text-text'
                  )} />
                  {item.name}
                </Link>
              )
            })}
          </nav>
          
          {/* User section in mobile */}
          <div className="border-t border-border p-4">
            <Link
              to="/dashboard/profile"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-2 mb-3 rounded-lg p-1 hover:bg-bg transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-accent-2 flex items-center justify-center">
                <User className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm text-text truncate max-w-[140px]">
                {user?.signInDetails?.loginId || 'User'}
              </span>
            </Link>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-text-muted hover:text-text bg-bg hover:bg-border/50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-panel border-r border-border">
          <div className="flex h-16 items-center px-4 border-b border-border">
            <Link to="/"><Logo size="md" /></Link>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/20 shadow-lg shadow-accent/10'
                      : 'text-text-muted hover:bg-bg hover:text-text border border-transparent'
                  )}
                >
                  <Icon className={clsx(
                    'mr-3 h-5 w-5 transition-colors',
                    isActive ? 'text-accent' : 'text-text-muted group-hover:text-text'
                  )} />
                  {item.name}
                </Link>
              )
            })}
          </nav>
          
          {/* User section in desktop */}
          <div className="border-t border-border p-4">
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-3 mb-3 rounded-lg p-1 hover:bg-bg transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent-2 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text truncate">
                  {user?.signInDetails?.loginId?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-text-muted truncate">
                  {user?.signInDetails?.loginId || ''}
                </p>
              </div>
            </Link>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-text-muted hover:text-text bg-bg hover:bg-border/50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex h-16 bg-panel/80 backdrop-blur-lg border-b border-border">
          <button
            type="button"
            className="px-4 text-text-muted hover:text-text focus:outline-none focus:ring-2 focus:ring-accent lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex flex-1 items-center justify-end px-4 gap-4">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-xs font-medium text-accent">All Systems Operational</span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6 sm:py-8">
          <div className="container-custom">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}